import { useCallback, useState } from "react";
import { clientEnv } from "../../lib/env";
import { safeTrack } from "../observability/tracking/safeTrack";
import { fbLoginCallback } from "../utils/callbackHandlers";
import type {
  FacebookLoginOptionsV4,
  FacebookLoginResponse,
} from "../types/facebook";
import { STORAGE_KEYS, setSessionItem } from "../utils/storageKeys";
import { sdkLogger } from "../utils/logger";

export interface LaunchResult {
  launch: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

interface LaunchDeps {
  appId?: string | null;
  configId?: string | null;
  callbackUrl?: string | null;
  cliSessionId?: string | null;
  state?: string | null;
  sdkReady: boolean;
}

interface RedirectUriResolution {
  facebookRedirectUri: string;
  rawEncodedRedirectParam: string | null;
  redirectMode: string | null;
}

interface LaunchBlockingState {
  error: string;
  logEvent?: string;
  logMessage?: string;
}

interface LoginResponseHandlerArgs {
  response: FacebookLoginResponse;
  callbackUrl: string;
  state: string | null | undefined;
  facebookRedirectUri: string;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
}

// This is the Meta embedded-signup flow version for `FB.login(..., { extras })`.
// It is intentionally separate from the Facebook SDK / Graph API version passed
// to `FB.init({ version })` in `useFacebookSdk`.
const EMBEDDED_SIGNUP_FLOW_VERSION = "v4" as const;

export function resolveFacebookRedirectUri(
  rawSearch: string,
  callbackUrl: string,
): RedirectUriResolution {
  let rawEncodedRedirectParam: string | null = null;
  let redirectMode: string | null = null;

  try {
    const redirectMatch = rawSearch.match(/[?&]redirect_uri=([^&]+)/);
    if (redirectMatch) {
      rawEncodedRedirectParam = redirectMatch[1];
    }

    const redirectModeMatch = rawSearch.match(/[?&]redirect_uri_mode=([^&]+)/);
    if (redirectModeMatch) {
      redirectMode = decodeURIComponent(redirectModeMatch[1]);
    }
  } catch {
    /* ignore */
  }

  const facebookRedirectUri =
    redirectMode === "raw" && rawEncodedRedirectParam
      ? decodeURIComponent(rawEncodedRedirectParam)
      : callbackUrl;

  return {
    facebookRedirectUri,
    rawEncodedRedirectParam,
    redirectMode,
  };
}

export function buildFbLoginOptions(
  configId: string,
  state: string | null | undefined,
): FacebookLoginOptionsV4 {
  return {
    config_id: configId,
    response_type: "code",
    override_default_response_type: true,
    state: state || undefined,
    extras: {
      version: EMBEDDED_SIGNUP_FLOW_VERSION,
      featureType: "whatsapp_business_app_onboarding",
      features: [
        "whatsapp_business_messaging",
        "whatsapp_business_phone_number",
      ],
    },
  };
}

export function getLaunchBlockingState({
  callbackUrl,
  configId,
  hasFacebookSdk,
  sdkReady,
}: {
  callbackUrl?: string | null;
  configId?: string | null;
  hasFacebookSdk: boolean;
  sdkReady: boolean;
}): LaunchBlockingState | null {
  if (!configId) {
    return {
      error:
        "Missing WhatsApp configuration. Please log in to Zapier and start the connection process from within your account.",
      logEvent: "launch_blocked_missing_config_id",
      logMessage: "Cannot launch without config_id",
    };
  }

  if (!sdkReady) {
    return {
      error: "Something went wrong. Please refresh the page and try again.",
    };
  }

  if (!callbackUrl) {
    return {
      error:
        "Missing callback URL. Please log in to Zapier and start the connection process from within your account.",
      logEvent: "launch_blocked_missing_callback_url",
      logMessage: "Cannot launch without redirect_uri",
    };
  }

  if (!hasFacebookSdk) {
    return {
      error: "Something went wrong. Please refresh the page and try again.",
      logEvent: "fb_sdk_missing_at_launch",
      logMessage: "Facebook SDK was marked ready but window.FB was unavailable",
    };
  }

  return null;
}

function redirectToCallbackWithError(
  callbackUrl: string,
  errorMessage: string,
  state: string | null | undefined,
) {
  try {
    const errorUrl = new URL(callbackUrl);
    errorUrl.searchParams.set("error", errorMessage);
    if (state) {
      errorUrl.searchParams.set("state", state);
    }
    window.location.href = errorUrl.toString();
  } catch {
    /* ignore redirect issues */
  }
}

export function handleFacebookLoginResponse({
  response,
  callbackUrl,
  state,
  facebookRedirectUri,
  setError,
  setLoading,
}: LoginResponseHandlerArgs) {
  const missingCodeError =
    "Facebook did not return an authorization code. Please try again.";
  const unexpectedResponseError =
    "Something went wrong while finishing Facebook signup. Please try again.";

  sdkLogger.info("fb_login_callback_fired", {
    has_response: !!response,
    status: response?.status,
    has_code: !!response?.authResponse?.code,
  });

  const code = response?.authResponse?.code;
  if (code) {
    fbLoginCallback(response, callbackUrl, state ?? null, facebookRedirectUri);
    return;
  }

  if (response?.status === "unknown" || response?.status === "not_authorized") {
    const errorMessage =
      response?.status === "unknown"
        ? "Please log in to Facebook to continue"
        : "You must authorize the app to continue";
    setError(errorMessage);
    setLoading(false);
    redirectToCallbackWithError(callbackUrl, errorMessage, state);
    return;
  }

  if (response?.status === "connected") {
    sdkLogger.error("fb_login_missing_code_for_connected_response", {
      has_auth_response: !!response?.authResponse,
    });
    setError(missingCodeError);
    setLoading(false);
    redirectToCallbackWithError(callbackUrl, missingCodeError, state);
    return;
  }

  if (response?.authResponse) {
    fbLoginCallback(response, callbackUrl, state ?? null, facebookRedirectUri);
    return;
  }

  sdkLogger.error("fb_login_unexpected_response", {
    has_auth_response: !!response?.authResponse,
    status: response?.status ?? null,
  });
  setError(unexpectedResponseError);
  setLoading(false);
  redirectToCallbackWithError(callbackUrl, unexpectedResponseError, state);
}

export function useLaunchWabaSignup({
  appId,
  configId,
  callbackUrl,
  cliSessionId,
  state,
  sdkReady,
}: LaunchDeps): LaunchResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const launch = useCallback(async () => {
    const blockingState = getLaunchBlockingState({
      configId,
      sdkReady,
      callbackUrl,
      hasFacebookSdk: typeof window !== "undefined" && !!window.FB,
    });
    if (blockingState) {
      setError(blockingState.error);
      if (blockingState.logEvent) {
        sdkLogger.error(blockingState.logEvent, {
          message: blockingState.logMessage,
        });
      }
      return;
    }
    const resolvedCallbackUrl = callbackUrl as string;
    const resolvedConfigId = configId as string;
    const facebookSdk = window.FB;
    if (!facebookSdk) {
      return;
    }

    // Debug: capture the raw encoded value of redirect_uri from the original query string
    const { facebookRedirectUri, rawEncodedRedirectParam, redirectMode } =
      resolveFacebookRedirectUri(
        typeof window !== "undefined" ? window.location.search : "",
        resolvedCallbackUrl,
      );

    // Check for redirect_uri encoding discrepancies
    try {
      const reEncoded = encodeURIComponent(facebookRedirectUri);
      if (rawEncodedRedirectParam && rawEncodedRedirectParam !== reEncoded) {
        sdkLogger.warn("redirect_uri_discrepancy", {
          raw_encoded_param: rawEncodedRedirectParam,
          reencoded: reEncoded,
          redirect_mode: redirectMode,
        });
      }
    } catch {
      /* ignore */
    }

    safeTrack("launch_initiated", {
      has_config: !!configId,
      sdk_ready: sdkReady,
      has_callback_url: !!callbackUrl,
      has_cli_session: !!cliSessionId,
      has_state: !!state,
      facebook_redirect_uri: facebookRedirectUri,
      cli_redirect_used: !!callbackUrl,
      environment: clientEnv.NEXT_PUBLIC_VERCEL_ENV,
    });

    setLoading(true);
    setError(null);

    // Clear any stale WhatsApp IDs from previous signup attempts
    // This ensures IDs in sessionStorage match the current signup flow
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(STORAGE_KEYS.phoneNumberId);
      window.sessionStorage.removeItem(STORAGE_KEYS.wabaId);
      window.sessionStorage.removeItem(STORAGE_KEYS.businessId);
    }

    // Persist callback info
    if (callbackUrl) setSessionItem(STORAGE_KEYS.callbackUrl, callbackUrl);
    if (state) setSessionItem(STORAGE_KEYS.state, state);

    try {
      // CRITICAL: Do NOT set redirect_uri in FB.login()
      //
      // Why? Zapier CLI generates its own redirect_uri (zapier.com/dashboard/auth/oauth/...)
      // and we cannot change it. If we set a different redirect_uri in FB.login(), we get
      // a mismatch error during token exchange.
      //
      // Solution: Call FB.login() WITHOUT redirect_uri. This makes the callback fire
      // in-page (popup/dialog mode), allowing us to:
      // 1. Access sessionStorage (still on wabasignup.zapier.com)
      // 2. Get the code from the callback
      // 3. Manually redirect to CLI callback with code + WhatsApp IDs
      //
      // The CLI will then use its own redirect_uri for token exchange, which matches
      // what Facebook expects (no redirect_uri, since we didn't provide one to FB.login).

      const fbLoginOptions = buildFbLoginOptions(resolvedConfigId, state);

      sdkLogger.info("fb_login_initiated", {
        config_id: resolvedConfigId,
        has_callback_url: !!resolvedCallbackUrl,
      });

      facebookSdk.login((response: FacebookLoginResponse) => {
        handleFacebookLoginResponse({
          response,
          callbackUrl: resolvedCallbackUrl,
          state,
          facebookRedirectUri,
          setError,
          setLoading,
        });
      }, fbLoginOptions);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(
        msg.includes("Popup blocked")
          ? msg
          : "Something went wrong. Please refresh the page and try again.",
      );
      setLoading(false);
    }
  }, [appId, configId, callbackUrl, cliSessionId, state, sdkReady]);

  return { launch, loading, error };
}
